import React, { useEffect, useRef } from 'react';
import { Form } from '@themesberg/react-bootstrap';
import { allUserPermissions, PERMISSION_MODULES } from '../utils/userPermissions';

const actionLabels = { view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete' };

const UserPermissionFields = ({ formik, existingUser = false }) => {
  const permissions = formik.values.permissions == null ? allUserPermissions() : formik.values.permissions;
  const selectAllRef = useRef(null);
  const selectedActions = PERMISSION_MODULES.flatMap(module =>
    module.actions.map(action => (permissions[module.key] || []).includes(action)));
  const allSelected = selectedActions.every(Boolean);
  const someSelected = selectedActions.some(Boolean);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [allSelected, someSelected]);

  const changePermission = (module, action, enabled) => {
    const current = permissions[module] || [];
    formik.setFieldValue('permissions', {
      ...permissions,
      [module]: enabled ? [...new Set([...current, action])] : current.filter(item => item !== action),
    });
  };
  const permissionErrors = Object.entries(formik.errors)
    .filter(([key]) => key === 'permissions' || key.startsWith('permissions.'))
    .flatMap(([, value]) => typeof value === 'string' ? [value] : []);

  return (
    <fieldset className="border rounded p-3 mb-3" disabled={formik.isSubmitting}>
      <legend className="h6 mb-2">User Permissions</legend>
      <p className="text-muted mb-3">
        Choose which actions this user can perform. {existingUser && formik.values.permissions == null
          ? 'This existing user currently has all actions enabled. Uncheck any actions you want to remove.'
          : 'Only selected actions are allowed.'}
      </p>
      <div className="form-check mb-3">
        <input
          ref={selectAllRef}
          id="permission-select-all"
          className="form-check-input"
          type="checkbox"
          checked={allSelected}
          onChange={event => formik.setFieldValue('permissions', event.target.checked ? allUserPermissions() : {})}
        />
        <label className="form-check-label fw-bold" htmlFor="permission-select-all">Select All</label>
      </div>
      <div className="table-responsive">
        <table className="table align-middle mb-0">
          <caption className="visually-hidden">Allowed actions by module</caption>
          <thead>
            <tr>
              <th scope="col">Module</th>
              {Object.entries(actionLabels).map(([action, label]) => <th key={action} scope="col" className="text-center">{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_MODULES.map(module => (
              <tr key={module.key}>
                <th scope="row">{module.label}</th>
                {Object.entries(actionLabels).map(([action, label]) => (
                  <td key={action} className="text-center">
                    {module.actions.includes(action) ? (
                      <Form.Check
                        type="checkbox"
                        id={'permission-' + module.key + '-' + action}
                        aria-label={module.label + ': ' + label}
                        className="d-inline-block mb-0"
                        checked={(permissions[module.key] || []).includes(action)}
                        onChange={event => changePermission(module.key, action, event.target.checked)}
                      />
                    ) : <span aria-label="Not available">-</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {permissionErrors.length > 0 && <div role="alert" className="text-danger mt-2">{permissionErrors.join(' ')}</div>}
    </fieldset>
  );
};

export default UserPermissionFields;
